import { useEffect, useMemo, useRef, useState } from 'react';
import './FundScreenerWidget.css';
import '../../Components/Common/typography.css';
import { Paper } from '@mui/material';
import {
  AssetTypeItem,
  ClassificationItem,
  FundCriteria,
  FundScreenerSortingType,
} from '../../types/fundScreener.type';
import { defaultFundCriteria } from '../../constants/fundScreener/fundScreener.constant';
import useFundScreenerColumn from '../../hooks/FundScreener/useFundScreenerColumn';
import { useFundScreenerData } from '../../hooks/FundScreener/useFundScreenerData';
import { useFundClassifications } from '../../hooks/FundScreener/useFundClassifications';
import { useFundAssetTypes } from '../../hooks/FundScreener/useFundAssetTypes';
import FundScreenerMenu from '../../Components/FundScreener/FundScreenerMenu';
import FundScreenerTable from '../../Components/FundScreener/FundScreenerTable';
import FundAdvancedCriteriaPopup from '../../Components/FundScreener/Criteria/FundAdvancedCriteriaPopup/FundAdvancedCriteriaPopup';
import FundSPAdvancedCriteriaPopup from '../../Components/FundScreener/Criteria/FundSPAdvancedCriteriaPopup/FundSPAdvancedCriteriaPopup';
import Pagination from '../../Components/Common/Pagination/Pagination';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import Layout from '../../Components/Common/Layout/Layout';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
import { CommonTooltipType } from '../../types/common.type';
import {
  paginate,
  PaginationType,
  getMaxPageNum,
} from '../../utils/pagination';
import { defaultPageNum, errorMsg } from '../../constants/common.constant';
import {
  BaseWidgetProps,
  WidgetMessage,
  WidgetHeightMessage,
} from '../../types/common.type';
import {
  Region,
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
import { getCssVariable } from '../../utils/utils';
import { formatLocaleNumber } from '../../helpers/number';

export interface FundScreenerWidgetProps extends BaseWidgetProps {
  token?: string;
  saleInCountryCode?: Region;
  onCallback?: (message: WidgetMessage) => void;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const FundScreenerWidget = ({
  token = '',
  saleInCountryCode = Region.AU,
  onLoadingChange,
  onError,
  theme = ThemeType.Direct,
  onCallback,
  onNoData,
  onHeightChange,
}: FundScreenerWidgetProps) => {
  theme = Object.values(ThemeType).includes(theme) ? theme : ThemeType.Direct;
  const themeColor = useMemo(
    () => getCssVariable('--color-brand', theme),
    [theme],
  );
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(widgetRef, WidgetNameSource.FundScreener, onHeightChange);
  const { width, isSmallScreen } = useContainerWidth(widgetRef);
  const msgItem = errorMsg.find((obj) => obj.key === 'fundScreener');

  const baselineAssetTypes = useFundAssetTypes(saleInCountryCode, token);
  const [assetTypes, setAssetTypes] =
    useState<AssetTypeItem[]>(baselineAssetTypes);
  useEffect(() => {
    setAssetTypes(baselineAssetTypes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineAssetTypes]);
  const baselineClassifications = useFundClassifications(
    saleInCountryCode,
    token,
  );
  const [classifications, setClassifications] = useState<ClassificationItem[]>(
    baselineClassifications,
  );
  useEffect(() => {
    setClassifications(baselineClassifications);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineClassifications]);
  const [criteria, setCriteria] = useState<FundCriteria[]>(defaultFundCriteria);
  const [tempCriteria, setTempCriteria] =
    useState<FundCriteria[]>(defaultFundCriteria);
  // Cleared during render (not in an effect) so every FundCriteriaSetting
  // row — including sibling rows whose own min/max fetch also reacts to
  // `saleInCountryCode` — sees the cleared value on the very first render
  // with the new market. An effect here would race: sibling effects run in
  // DOM order, so e.g. NAV's minmax fetch could fire (reading the stale
  // companyName from props) before Fund management company's own effect
  // gets a chance to clear it.
  const prevMarketForCompanyRef = useRef(saleInCountryCode);
  if (prevMarketForCompanyRef.current !== saleInCountryCode) {
    prevMarketForCompanyRef.current = saleInCountryCode;
    const clearCompany = (list: FundCriteria[]) =>
      list.map((item) =>
        item.id === 'fundCompany' ? { ...item, companyName: undefined } : item,
      );
    setCriteria(clearCompany);
    setTempCriteria(clearCompany);
  }
  const [openAdvancedCriteria, setOpenAdvancedCriteria] = useState(false);
  const [openSPAdvancedCriteria, setOpenSPAdvancedCriteria] = useState(false);

  useEffect(() => {
    if (openSPAdvancedCriteria) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [openSPAdvancedCriteria]);

  const [maxPageNum, setMaxPageNum] = useState(defaultPageNum);
  const RESULTS_PER_PAGE = useMemo(
    () => (isSmallScreen ? 10 : 20),
    [isSmallScreen],
  );
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationType>(
    paginate(page, RESULTS_PER_PAGE, 0, maxPageNum),
  );
  // Default sort: fund name A-Z, 0-9.
  const [sorting, setSorting] = useState<FundScreenerSortingType>({
    id: 'name',
    asc: true,
  });
  const [resetTrigger, setResetTrigger] = useState(0);

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (width > 0) {
      setMaxPageNum(getMaxPageNum(width, false, false, false));
    }
  }, [width]);

  useEffect(() => {
    if (!isInitialMount.current) {
      setPage(1);
    }
  }, [criteria, assetTypes, classifications, RESULTS_PER_PAGE]);

  const activeAssetType =
    assetTypes.find((item) => item.activated)?.value || 'ALL';
  const activeClassification =
    classifications.find((item) => item.activated)?.value || 'ALL';

  const { data, total, isLoading, isEmptyData } = useFundScreenerData({
    market: saleInCountryCode,
    token,
    page,
    pageSize: RESULTS_PER_PAGE,
    assetType: activeAssetType,
    classification: activeClassification,
    criteria,
    sorting,
    resetTrigger,
    onLoadingChange,
    onError,
    onNoData,
  });

  useEffect(() => {
    setPagination(paginate(page, RESULTS_PER_PAGE, total, maxPageNum));
    isInitialMount.current = false;
  }, [total, page, maxPageNum]);

  useEffect(() => {
    setTempCriteria(criteria);
  }, [openAdvancedCriteria, openSPAdvancedCriteria]);

  const handleGetResults = () => {
    setOpenAdvancedCriteria(false);
    setOpenSPAdvancedCriteria(false);
    setCriteria(tempCriteria);
  };

  const handleCancel = () => {
    setOpenAdvancedCriteria(false);
    setOpenSPAdvancedCriteria(false);
  };

  const handleResetAll = () => {
    setCriteria(defaultFundCriteria);
    setAssetTypes(baselineAssetTypes);
    setClassifications(baselineClassifications);
  };

  const handleRefresh = () => {
    setResetTrigger((prev) => prev + 1);
  };

  const handleFundClick = (ric: string) => {
    if (!ric) return;
    const message: WidgetMessage = {
      // Key is "symbol" to match every other widget's OnStockClick payload;
      // value is still the RIC.
      data: { symbol: ric },
      source: 'fundScreener',
      type: WidgetMessageType.OnStockClick,
    };
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify(message),
        '*',
      );
    }
    window.parent.postMessage(JSON.stringify(message), '*');
    onCallback?.(message);
  };

  const columns = useFundScreenerColumn({
    selectedCriteria: criteria.filter((item) => item.selected),
    market: saleInCountryCode,
    page,
    size: RESULTS_PER_PAGE,
    isSmallScreen,
    sorting,
    setSorting,
    setTooltip,
    handleFundClick,
  });

  return (
    <Layout theme={theme}>
      <div
        ref={widgetRef}
        className={`fund-screener-container${
          openAdvancedCriteria ? ' fund-screener-container-criteria-open' : ''
        }`}
      >
        <div className="fund-screener">
          <FundScreenerMenu
            isSmallScreen={isSmallScreen}
            assetTypes={assetTypes}
            setAssetTypes={setAssetTypes}
            classifications={classifications}
            setClassifications={setClassifications}
            criteria={criteria}
            setOpenAdvancedCriteria={setOpenAdvancedCriteria}
            setOpenSPAdvancedCriteria={setOpenSPAdvancedCriteria}
            onResetAll={handleResetAll}
            onRefresh={handleRefresh}
          />

          <Paper
            elevation={0}
            sx={{
              boxSizing: 'border-box',
              textAlign: 'start',
              padding: isSmallScreen ? '16px 16px' : '16px 32px 8px 32px',
            }}
          >
            <p
              className="body-m-regular"
              style={{ margin: 0, color: 'var(--text-instrument)' }}
            >
              {`Showing ${formatLocaleNumber(data.length > 0 ? (page - 1) * RESULTS_PER_PAGE + 1 : 0)}-${formatLocaleNumber(Math.min(page * RESULTS_PER_PAGE, total))} of ${formatLocaleNumber(total)} results`}
            </p>
          </Paper>

          {isEmptyData && !isLoading ? (
            <div style={{ height: 600 }}>
              <ErrorMessage msg={msgItem} />
            </div>
          ) : (
            <div
              className={`fund-screener-table-pagination-wrapper ${isSmallScreen ? 'fund-screener-table-pagination-wrapper-mobile' : ''}`}
            >
              {isLoading && data.length === 0 && !onLoadingChange ? (
                <div style={{ height: 600 }}>
                  <Loading />
                </div>
              ) : (
                <>
                  {isLoading && !onLoadingChange && (
                    <div className="fund-screener-table-loading-overlay">
                      <Loading />
                    </div>
                  )}
                  <FundScreenerTable
                    columns={columns}
                    data={data}
                    isSmallScreen={isSmallScreen}
                  />
                </>
              )}
              <div className="fund-screener-pagination-container">
                <Pagination
                  pagination={pagination}
                  onPageClick={(targetPage: number) => setPage(targetPage)}
                  haveLastPage={true}
                />
              </div>
            </div>
          )}

          {openAdvancedCriteria && (
            <>
              <div className="fund-screener-backdrop" />
              <FundAdvancedCriteriaPopup
                market={saleInCountryCode}
                tempCriteria={tempCriteria}
                setTempCriteria={setTempCriteria}
                onGetResults={handleGetResults}
                onCancel={handleCancel}
                token={token}
              />
            </>
          )}
        </div>

        {isSmallScreen && openSPAdvancedCriteria && (
          <FundSPAdvancedCriteriaPopup
            themeColor={themeColor}
            market={saleInCountryCode}
            assetTypes={assetTypes}
            setAssetTypes={setAssetTypes}
            classifications={classifications}
            setClassifications={setClassifications}
            tempCriteria={tempCriteria}
            setTempCriteria={setTempCriteria}
            setOpenSPAdvancedCriteria={setOpenSPAdvancedCriteria}
            onGetResults={handleGetResults}
            onCancel={handleCancel}
            token={token}
          />
        )}

        <CommonTooltip
          ref={tooltipRef}
          content={tooltip.content}
          show={tooltip.show}
          x={tooltip.x}
          y={tooltip.y}
        />
      </div>
    </Layout>
  );
};

export default FundScreenerWidget;
